import * as React from 'react';

import { DragPreviewCard } from './DragPreviewCard';

const PREVIEW_PREPARATION_DISTANCE = 1;
const PREVIEW_DISPLAY_DISTANCE = 4;
const CURSOR_OFFSET_X = 12;
const APPEARANCE_DURATION_MS = 100;
const APPEARANCE_START_OPACITY = 0.82;
const ACTIVE_PAGE_DRAG_PREVIEW_ATTRIBUTE = 'data-page-drag-preview-active';

export type PageDragPreviewRef = {
    hide: () => void;
    isPrepared: () => boolean;
};

/**
 * Shows page-drag feedback before dnd-kit reaches its 10px activation threshold, then remains as
 * the visible preview while dnd-kit handles target detection and dropping.
 */
export const PageDragPreview = React.memo(
    React.forwardRef<PageDragPreviewRef>(function PageDragPreview(_props, ref) {
        const previewRef = React.useRef<HTMLDivElement>(null);
        const cardRef = React.useRef<HTMLDivElement>(null);
        const labelRef = React.useRef<HTMLDivElement>(null);
        // Pointer movement is frequent, so keep the pending gesture outside React state to avoid
        // re-rendering the provider before dnd-kit has activated the drag.
        const pendingDragRef = React.useRef<{
            pointerId: number;
            draggable: HTMLElement;
            startX: number;
            startY: number;
        } | null>(null);
        const appearanceAnimationRef = React.useRef<Animation | null>(null);

        const hide = React.useCallback(() => {
            // Cleanup can happen before or after dnd-kit activates. Always hide the early
            // preview and restore the source row in both cases.
            pendingDragRef.current?.draggable.removeAttribute(ACTIVE_PAGE_DRAG_PREVIEW_ATTRIBUTE);
            pendingDragRef.current = null;
            appearanceAnimationRef.current?.cancel();
            appearanceAnimationRef.current = null;
            if (previewRef.current) {
                previewRef.current.style.visibility = 'hidden';
            }
        }, []);

        React.useImperativeHandle(
            ref,
            () => ({
                hide,
                isPrepared: () => pendingDragRef.current !== null,
            }),
            [hide]
        );

        React.useEffect(() => {
            // dnd-kit does not expose pointer movement before its activation threshold.
            // So we observe the pointer to display to drag preview without having to wait
            // the actual drag work to start which would delay the preview's appearance.
            const onPointerDown = (event: PointerEvent) => {
                if (
                    pendingDragRef.current ||
                    !event.isPrimary ||
                    event.button !== 0 ||
                    !(event.target instanceof Element)
                ) {
                    return;
                }

                hide();

                // Page rows opt into this early preview and provide its label through one element.
                const draggable = event.target.closest<HTMLElement>(
                    '[data-page-drag-preview-label]'
                );
                const label = draggable?.dataset.pageDragPreviewLabel;

                if (!label) {
                    return;
                }

                pendingDragRef.current = {
                    pointerId: event.pointerId,
                    draggable,
                    startX: event.clientX,
                    startY: event.clientY,
                };

                if (labelRef.current) {
                    labelRef.current.textContent = label;
                }
            };

            const onPointerMove = (event: PointerEvent) => {
                const pendingDrag = pendingDragRef.current;
                const preview = previewRef.current;

                if (!pendingDrag || event.pointerId !== pendingDrag.pointerId || !preview) {
                    return;
                }

                const distance = Math.hypot(
                    event.clientX - pendingDrag.startX,
                    event.clientY - pendingDrag.startY
                );
                preview.style.transform = `translate3d(${event.clientX + CURSOR_OFFSET_X}px, ${event.clientY}px, 0) translateY(-50%)`;

                if (distance >= PREVIEW_PREPARATION_DISTANCE) {
                    // Apply the source-row styles first so they are ready when the preview appears.
                    pendingDrag.draggable.toggleAttribute(ACTIVE_PAGE_DRAG_PREVIEW_ATTRIBUTE, true);
                }

                if (preview.style.visibility === 'hidden' && distance >= PREVIEW_DISPLAY_DISTANCE) {
                    // A short opacity-only transition softens the preview without delaying it or
                    // causing a layout shift. Respect the user's reduced-motion preference.
                    if (
                        cardRef.current &&
                        !window.matchMedia('(prefers-reduced-motion: reduce)').matches
                    ) {
                        appearanceAnimationRef.current = cardRef.current.animate(
                            [{ opacity: APPEARANCE_START_OPACITY }, { opacity: 1 }],
                            {
                                duration: APPEARANCE_DURATION_MS,
                                easing: 'ease-out',
                                fill: 'both',
                            }
                        );
                    }
                    preview.style.visibility = 'visible';
                }
            };

            const onPointerEnd = (event: PointerEvent) => {
                // Ignore another finger or pointer ending while the initiating pointer is active.
                if (event.pointerId === pendingDragRef.current?.pointerId) {
                    hide();
                }
            };

            const onKeyDown = (event: KeyboardEvent) => {
                if (event.key === 'Escape') {
                    hide();
                }
            };

            // Capture events so nested controls cannot prevent preview preparation or cleanup.
            document.addEventListener('pointerdown', onPointerDown, true);
            document.addEventListener('pointermove', onPointerMove, true);
            document.addEventListener('pointerup', onPointerEnd, true);
            document.addEventListener('pointercancel', onPointerEnd, true);
            document.addEventListener('keydown', onKeyDown, true);
            window.addEventListener('blur', hide);

            return () => {
                document.removeEventListener('pointerdown', onPointerDown, true);
                document.removeEventListener('pointermove', onPointerMove, true);
                document.removeEventListener('pointerup', onPointerEnd, true);
                document.removeEventListener('pointercancel', onPointerEnd, true);
                document.removeEventListener('keydown', onKeyDown, true);
                window.removeEventListener('blur', hide);
                hide();
            };
        }, [hide]);

        return (
            <div
                ref={previewRef}
                aria-hidden
                className="pointer-events-none fixed top-0 left-0 z-1000 will-change-transform"
                style={{ visibility: 'hidden' }}
            >
                <DragPreviewCard
                    ref={cardRef}
                    icon="FileText"
                    label=""
                    labelRef={labelRef}
                    className="will-change-opacity"
                />
            </div>
        );
    })
);
